/**
 * Link safety (architecture §11.2 step 4, §11.5): an href is an absolute `https:`, `http:` or `mailto:` URL, or a
 * same-origin path; ≤ 2,048 characters; no embedded credentials. Checked at write and again at render (imported data
 * might predate validation). The scheme is read from the raw string, before any URL parsing that would strip tabs,
 * newlines or entities, so `java\tscript:`, `jav&#x09;ascript:` and confusable schemes never become live links.
 */
const SCHEME = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;
const ALLOWED = new Set(['http', 'https', 'mailto']);

export function safeHref(href: unknown, publicUrl: string): string | null {
  if (typeof href !== 'string' || href.length === 0 || href.length > 2048) return null;
  // control characters, whitespace or backslashes anywhere: browsers normalize them away (tab/newline stripping)
  if (/[\x00-\x20\x7f-\xa0\u2028\u2029\\]/.test(href)) return null;
  const m = SCHEME.exec(href);
  if (m) {
    if (!ALLOWED.has(m[1]!.toLowerCase())) return null;
    let url: URL;
    try {
      url = new URL(href);
    } catch {
      return null;
    }
    if (url.username !== '' || url.password !== '') return null;
    if (m[1]!.toLowerCase() !== 'mailto' && url.hostname === '') return null;
    return href;
  }
  // relative: only a same-origin path (`/x`), never protocol-relative (`//host`)
  if (!href.startsWith('/') || href.startsWith('//')) return null;
  try {
    const base = new URL(publicUrl);
    const url = new URL(href, base);
    return url.origin === base.origin ? href : null;
  } catch {
    return null;
  }
}
